/**
 * S-24/S-25/S-26 시즌 대시보드·스냅샷·시즌 종료 안내 목 데이터 (F-20/F-21/F-22, §6).
 * R-16 금지 5항목(예상 TYCOON 수령량·IP 환산가·1점당 예상치·USDT 가치·수익률)은
 * 이 모듈이 절대 계산·노출하지 않는다 — §6.6.2 QA 게이트.
 * TODO(server): 순위/참여자/리더보드/스냅샷 일정은 전부 서버 값으로 교체.
 */
import { SEASON_CONFIG, type SeasonConfig } from "@/lib/season";
import { SEASON_TIERS, type SeasonTier } from "./mockdb";

/** 목 순위 정보 — api.getHomeSummary.rank가 아직 null이라 표시용 고정값 */
export const MOCK_RANKING = {
  rank: 1_832,
  participants: 18_342,
  topUserIp: 480_500,
  /** 다음 티어 경계까지 필요한 IP 차이 (§6.6.2 허용 항목) */
  ipToNextTier: 3_200,
} as const;

/** 순위 → 백분위 티어 — 리더보드 행 뱃지·티어 경계 배너 공용 */
export function tierForRank(rank: number, participants: number = MOCK_RANKING.participants): SeasonTier {
  const percentile = (rank / participants) * 100;
  return SEASON_TIERS.find((t) => percentile <= t.topPercent) ?? SEASON_TIERS[SEASON_TIERS.length - 1];
}

export type SeasonStanding = {
  rank: number;
  participants: number;
  /** 상위 백분위 (0~100) */
  percentile: number;
  tier: SeasonTier;
  ipToTop: number;
  ipToNextTier: number;
};

export function computeStanding(ip: number): SeasonStanding {
  const { rank, participants, topUserIp, ipToNextTier } = MOCK_RANKING;
  const percentile = (rank / participants) * 100;
  const tier = SEASON_TIERS.find((t) => percentile <= t.topPercent) ?? SEASON_TIERS[SEASON_TIERS.length - 1];
  return {
    rank,
    participants,
    percentile,
    tier,
    ipToTop: Math.max(0, topUserIp - ip),
    ipToNextTier,
  };
}

export type LeaderboardRow = {
  rank: number;
  name: string;
  ip: number;
  /** 전일 대비 순위 변동 — 부호 명시 렌더, 절대 숨김 금지 (§2E) */
  delta: number;
  me?: boolean;
};

/**
 * 상위권 + 내 주변 순위(순위가 1 초과로 건너뛰면 화면에서 생략 행으로 렌더).
 * 1834↔1835 사이가 Top 10% 티어 경계(참여자 18,342 기준) — 경계 배너 데모.
 * TODO(server): 시즌 리더보드 RPC.
 */
export const MOCK_LEADERBOARD: LeaderboardRow[] = [
  { rank: 1, name: "@grail_hunter", ip: 480_500, delta: 0 },
  { rank: 2, name: "@holo_archive", ip: 402_130, delta: 1 },
  { rank: 3, name: "@ellie_desk", ip: 355_780, delta: -1 },
  { rank: 1_830, name: "@vault_keeper", ip: 12_940, delta: 2 },
  { rank: 1_831, name: "@base_stacker", ip: 12_610, delta: -1 },
  { rank: 1_832, name: "You", ip: 12_480, delta: 3, me: true },
  { rank: 1_833, name: "@pack_ratio", ip: 12_455, delta: 0 },
  { rank: 1_834, name: "@mint_cond", ip: 12_390, delta: -2 },
  { rank: 1_835, name: "@shelf_life", ip: 12_310, delta: 1 },
  { rank: 1_836, name: "@dust_cap", ip: 12_150, delta: -3 },
];

/** 티어 필터 뷰의 순위 구간 — SEASON_TIERS topPercent 경계 × 참여자 수 */
export function tierRankRange(
  tier: number,
  participants: number = MOCK_RANKING.participants,
): { from: number; to: number } {
  const idx = SEASON_TIERS.findIndex((t) => t.tier === tier);
  const prevPercent = idx > 0 ? SEASON_TIERS[idx - 1].topPercent : 0;
  const topPercent = SEASON_TIERS[idx]?.topPercent ?? 100;
  return {
    from: Math.floor((prevPercent / 100) * participants) + 1,
    to: Math.floor((topPercent / 100) * participants),
  };
}

/** 티어 뷰 목 행 이름 순환 목록 — 결정론(랜덤 금지) */
const TIER_HANDLES = [
  "@grail_hunter",
  "@holo_archive",
  "@ellie_desk",
  "@vault_keeper",
  "@base_stacker",
  "@pack_ratio",
  "@mint_cond",
  "@shelf_life",
  "@dust_cap",
  "@slab_index",
  "@rip_and_ship",
  "@top_loader",
];

/**
 * 티어별 목 리더보드 — null = 전체(MOCK_LEADERBOARD), 티어 선택 시 해당 순위
 * 구간의 연속 9행을 결정론 생성(IP·±는 rank 파생, 이름은 핸들 순환).
 * 내 티어 선택 시 내 행(rank 1,832) 포함 윈도우로 정렬.
 * TODO(server): 티어별 리더보드 RPC로 교체.
 */
export function leaderboardForTier(tier: number | null): LeaderboardRow[] {
  if (tier === null) return MOCK_LEADERBOARD;
  const { rank: myRank, topUserIp } = MOCK_RANKING;
  const { from, to } = tierRankRange(tier);
  const count = Math.min(9, to - from + 1);
  const mine = myRank >= from && myRank <= to;
  // 내 티어면 내 순위 주변, 아니면 티어 상단부터 연속 구간
  const start = mine ? Math.max(from, Math.min(myRank - 4, to - (count - 1))) : from;
  const me = MOCK_LEADERBOARD.find((r) => r.me);
  return Array.from({ length: count }, (_, i) => {
    const rank = start + i;
    if (mine && me && rank === myRank) return me;
    return {
      rank,
      name: TIER_HANDLES[rank % TIER_HANDLES.length],
      // ponytail: 목 IP 곡선 — rank 1=480,500 / rank 1,832≈12.5k 를 잇는 멱함수
      ip: Math.round(topUserIp * Math.pow(rank, -0.486)),
      delta: (rank % 7) - 3,
    };
  });
}

export type SnapshotDeadline = {
  label: string;
  /** ISO(UTC). null = 서버 공지 대기 */
  at: string | null;
  note?: string;
};

/**
 * §6.3.2 스냅샷 공지 항목 — 종료일·스냅샷 기준 시각(UTC)·카드 처리 마감·
 * 재매입/소각 신청 마감·자산 정산 기준. 마감 간격은 목(종료 12h/24h 전).
 * TODO(server): 서버 공지값 그대로 표시 — 프론트 하드코딩 금지.
 */
export function snapshotSchedule(config: SeasonConfig = SEASON_CONFIG): SnapshotDeadline[] {
  if (!config.endsAt) return [];
  const end = Date.parse(config.endsAt);
  const iso = (ms: number) => new Date(ms).toISOString();
  return [
    { label: "Season ends", at: config.endsAt },
    { label: "Final IP snapshot", at: config.endsAt, note: "All times in UTC" },
    { label: "Card processing closes", at: iso(end - 12 * 3_600_000) },
    { label: "Buyback & burn requests close", at: iso(end - 24 * 3_600_000) },
    {
      label: "Asset redemption cutoff",
      at: null,
      note: "Per the schedule announced before season end",
    },
  ];
}

/** S-26 카드 유형별 잔존 표 (§6.10.2) — 7행, 결과는 1줄 데이터형 문구만 */
export const CARD_RETENTION_ROWS: { type: string; outcome: string }[] = [
  { type: "Base / Holo", outcome: "Card & IP removed — album history kept" },
  { type: "Rare (not asset-linked)", outcome: "Per season policy announced before end" },
  { type: "Asset-linked rare", outcome: "IP removed — card & linked asset stay yours" },
  { type: "Settled (asset redeemed) card", outcome: "Burned after settlement — IP counts to season end" },
  { type: "RP Point Card", outcome: "Per usage status & product policy" },
  { type: "Redemption Card", outcome: "Per its exchange window & terms" },
  { type: "Unopened Free Packs", outcome: "Policy announced before season end" },
];

/** §6.8 에어드랍 자격 체크리스트 — done은 목 상태, to = 미충족 항목의 해결 화면
 * (improvements §M-5: 'Action needed'가 막다른 표시가 되지 않게). TODO(server): 계정별 자격 조회 */
export const ELIGIBILITY_CHECKLIST: { label: string; done: boolean; to?: string }[] = [
  { label: "Active TycoonEX account in good standing", done: true },
  { label: "Minimum season participation met", done: true },
  { label: "Eligible card IP held (min. IP threshold)", done: true },
  { label: "Airdrop wallet registered", done: false, to: "/profile/missions/links" },
  { label: "KYC completed (when required)", done: false, to: "/profile/kyc" },
  { label: "No abuse record", done: true },
  { label: "Not in a restricted region", done: true },
  { label: "Required verification done within the deadline", done: false },
];

/** §6.9.1 정산 진행 트래커 단계 (스냅샷→검증→티어 확정→지급→결과 공지) */
export const SETTLEMENT_STEPS = [
  "Final IP snapshot",
  "Data verification",
  "Tier finalization",
  "Airdrop distribution",
  "Results notice",
] as const;

/** 현재 정산 단계 인덱스 — -1 = 시즌 진행 중(정산 미시작). TODO(server) */
export const MOCK_SETTLEMENT_STEP = -1;

/** "2026-10-09 00:00 UTC" — 스냅샷/마감 시각 표기 공용 포맷 */
export function formatUtc(isoDate: string): string {
  return `${isoDate.slice(0, 10)} ${isoDate.slice(11, 16)} UTC`;
}
